import { useState, useRef, useEffect } from 'react'
import { Collection } from '../../../reducers/types/reducerTypes.js'
import {
  ComcastErrorType,
  triggerSentryError,
} from '../../../../../shared/helpers.js'
import { contentServiceGet } from '../../../lib/utils.js'
import { getSearch } from '../../../actions/contentActions.js'
import {
  checkShowsAndVideoAssetContentExistence,
  concatNextContent,
} from '../utils'
import { useAppSelector } from '../../../hooks/useStore'
import tts from '../../../lib/TTS.js'

const TTS_ERROR_MESSAGE =
  'Sorry, there was an unexpected error with search. Please check back later.'
const TTS_SEARCHING_MESSAGE = (searchQuery: string) =>
  `Searching for ${searchQuery}`
const SEARCH_ERROR_MESSAGE = 'An error occurred while searching'
const SERACH_RESULT_ERROR = 'setSearchResult'
const INITIAL_SEARCH_RESULT = 'setInitialSearchResults'
const NEXT_CAROUSEL_RESULT = 'setNextCarouselResult'

export const useSearchResult = () => {
  const [collectionsReady, setCollectionsReady] = useState(false)
  const [nextContentUrl, setNextContentUrl] = useState('')
  const [searchResultCollection, setSearchResultCollections] = useState<
    Collection<unknown>[]
  >([])
  const [initialCollection, setInitialCollection] = useState<
    Collection<unknown>[]
  >([])
  const stationId = useAppSelector((state) => state.station.station_id)
  const searchQueryRef = useRef<string>('')
  const [fetchingNextCarouselResult, setFetchingNextCarouselResult] =
    useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (collectionsReady && searchResultCollection.length === 0) {
      tts.addTextToRead(
        `We couldn't find any results for ${searchQueryRef.current}.`
      )
    }
  }, [setCollectionsReady, searchResultCollection])

  const setSearchResult = async (searchQuery: string) => {
    tts.addTextToRead(TTS_SEARCHING_MESSAGE)
    setCollectionsReady(false)
    try {
      searchQueryRef.current = searchQuery
      const response = await getSearch(stationId, searchQuery)
      const hasContentToDisplay = checkShowsAndVideoAssetContentExistence(
        response.collections
      )
      if (response) {
        setSearchResultCollections(
          hasContentToDisplay ? response.collections : []
        )
        setNextContentUrl(response.meta?.links?.next || '')
        setCollectionsReady(true)
      } else {
        throw new Error(SEARCH_ERROR_MESSAGE)
      }
    } catch (error) {
      setCollectionsReady(true)
      handleError(error, SERACH_RESULT_ERROR)
    }
  }

  const setInitialSearchResults = async () => {
    setCollectionsReady(false)

    if (initialCollection.length > 0) {
      setSearchResultCollections(initialCollection)
      setCollectionsReady(true)
      return
    }

    try {
      const response = await getSearch(stationId, '')
      const collections = response.collections.filter((element) => {
        return 'undefined' !== typeof element.content
      })

      setSearchResultCollections(collections)
      setCollectionsReady(true)
      setInitialCollection(collections)
    } catch (error) {
      console.error(error)
      setCollectionsReady(true)
      handleError(error, INITIAL_SEARCH_RESULT)
    }
  }

  const setNextCarouselResult = async () => {
    if (!fetchingNextCarouselResult && nextContentUrl) {
      setFetchingNextCarouselResult(true)
      try {
        const response = await contentServiceGet(nextContentUrl)
        const nextShowResults = concatNextContent(
          response.collections,
          'show-results',
          searchResultCollection
        )
        const nextVideoResults = concatNextContent(
          response.collections,
          'videoasset-results',
          searchResultCollection
        )

        setNextContentUrl(response.meta.links.next || '')
        setFetchingNextCarouselResult(false)

        const auxCollections = [...searchResultCollection]
        auxCollections.forEach((collection) => {
          if (collection.slug === 'show-results') {
            collection.content = nextShowResults.content
          } else if (collection.slug === 'videoasset-results') {
            collection.content = nextVideoResults.content
          }
        })

        setSearchResultCollections(auxCollections)
      } catch (error) {
        console.error(error)
        setFetchingNextCarouselResult(false)
        setSearchResultCollections([])
        handleError(error, NEXT_CAROUSEL_RESULT)
      }
    }
  }

  const handleError = (err: Error, errorString: string) => {
    console.log('err', err)
    setError(err)
    tts.addTextToRead(TTS_ERROR_MESSAGE)
    triggerSentryError(errorString, 'SearchScreen', {
      errorMessage: err.message ?? 'Undefined search message (default)',
      errorLocation: 'SearchScreen',
      errorType: ComcastErrorType.network,
    })
  }

  return {
    searchQueryRef,
    collectionsReady,
    searchResultCollection,
    initialCollection,
    setSearchResult,
    setInitialSearchResults,
    setNextCarouselResult,
    error,
  }
}
